import React, { useState, useEffect } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import {
  handleAppleAuth,
  handleSignIn,
  handleSignUp,
  handleGoogleAuth,
} from "../../redux/actions/auth";
import { useDispatch } from "react-redux";
import logo from "../../assets/images/logo.png";
import { FcGoogle } from "react-icons/fc";
import { FaApple } from "react-icons/fa";
import { toast } from "sonner";
import { usePostHog } from "posthog-js/react";
import { useSelector } from "react-redux";
function Authentication() {
  // get current route
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const posthog = usePostHog();
  const currentRoute = location.pathname.slice(1);

  // state variables
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);
  const [loginData, setLoginData] = useState({
    name: "",
    email: "",
    password: "",
    termsAccepted: false,
  });

  useEffect(() => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("setupModalStatus");
  }, []);

  // handle Input
  let name, value;
  const handleInputs = (e) => {
    name = e.target.name;
    value = e.target.value;
    setLoginData({ ...loginData, [name]: value });
  };

  // handle submit
  const handleSubmit = async (e) => {
    try {
      setLoading(true);
      e.preventDefault();
      const emailRegex =
        /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i;
      // validate email
      if (!emailRegex.test(loginData.email)) {
        toast.error("Invalid email!");
        return;
      }

      if (currentRoute === "signup") {
        // check required values
        if (!loginData.email || !loginData.password || !loginData.name) {
          toast.error("Please fill all the required fields!");
          return;
        }
        // check t&c
        if (!loginData.termsAccepted) {
          toast.error("Please accept terms & conditions!");
          return;
        }
        // validate password
        if (loginData.password.trim() === "" || loginData.password.length < 8) {
          toast.error("Password must be atleast 8 characters long");
          return;
        }
        let res = await handleSignUp(loginData);
        if (res) {
          dispatch({
            type: "SET_USER",
            payload: {
              email: loginData.email,
              name: loginData.name,
            },
          });
          localStorage.setItem(
            "user",
            JSON.stringify({
              email: loginData.email,
              name: loginData.name,
            })
          );
          posthog.identify(loginData.email, {
            name: loginData.name,
          });
          posthog.capture("user_signup", {
            email: loginData.email,
            name: loginData.name,
          });
          navigate("/payment-options");
        } else {
          dispatch({
            type: "CHANGE_ALERT_STATE",
            alerttype: "failed",
            heading: "Something went wrong!",
          });
        }
      } else {
        // check required values
        if (!loginData.email || !loginData.password) {
          toast.error("Please fill all the required fields!");
          return;
        }
        let res = await handleSignIn(loginData);
        if (res.success) {
          dispatch({
            type: "SET_USER",
            payload: {
              email: res.user?.email,
              name: res.user?.name,
            },
          });
          localStorage.setItem(
            "user",
            JSON.stringify({
              email: res.user?.email,
              name: res.user?.name,
            })
          );
          posthog.identify(res.user?.email, {
            name: res.user?.name,
          });
          posthog.capture("user_signin", {
            email: res.user?.email,
            name: res.user?.name,
          });
          navigate(res.redirect);
        } else {
          dispatch({
            type: "CHANGE_ALERT_STATE",
            alerttype: "failed",
            heading: "Something went wrong!",
          });
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuthClick = async () => {
    try {
      setGoogleLoading(true);
      const res = await handleGoogleAuth(
        currentRoute === "signup" ? "signup" : "login"
      );
      if (res && !res?.subscriber) {
        dispatch({
          type: "SET_USER",
          payload: {
            email: res?.email,
            name: res?.name,
          },
        });
        localStorage.setItem(
          "user",
          JSON.stringify({
            email: res?.email,
            name: res?.name,
          })
        );
        navigate("/payment-options");
      } else if (res?.subscriber) {
        localStorage.setItem(
          "user",
          JSON.stringify({
            email: res?.email,
            name: res?.name,
          })
        );
        navigate("/dashboard");
      }
    } catch (error) {
      console.log(error, "Error in googleAuth");
      toast.error("Something went wrong!");
    } finally {
      setGoogleLoading(false);
    }
  };
  const handleAppleAuthClick = async () => {
    try {
    setAppleLoading(true);
    const res = await handleAppleAuth(
      currentRoute === "signup" ? "signup" : "login"
    );
    if (res?.email && res?.name) {
      dispatch({
        type: "SET_USER",
        payload: {
          email: res?.email,
          name: res?.name,
        },
      });
      localStorage.setItem(
        "user",
        JSON.stringify({
          email: res?.email,
          name: res?.name,
        })
      );
      navigate("/payment-options");
    } else {
        toast.error("Something went wrong!");
      }
    } catch (error) {
      console.log(error, "Error in appleAuth");
      toast.error("Something went wrong!");
    } finally {
      setAppleLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen place-items-center">
      <div className="sm:max-w-lg xs:max-w-xm w-full">
        <img
          src={logo}
          alt="logo"
          className="text-center mx-auto w-[40%] h-auto"
        />
        <p className="text-3xl text-center font-bold text-[#101828] ease-in duration-300 mt-2">
          {currentRoute === "signup"
            ? "Create an account"
            : "Log In to your account"}
        </p>
        <p className="text-center text-gray-500 mt-1 pb-2">
          {currentRoute === "signup"
            ? "Start your 7-day free trial."
            : "Welcome back! Please enter your details."}
        </p>
        <form onSubmit={handleSubmit} className="px-7 sm:px-0">
          {currentRoute === "signup" && (
            <div className="mt-2">
              <label htmlFor="email" className="text-[#4E5674] font-[500]">
                Name *
              </label>
              <div className="mb-4 mt-1 flex items-center rounded-lg border-2 py-2 px-3">
                <input
                  className="border-none outline-none bg-transparent w-full "
                  type="text"
                  name="name"
                  id="text"
                  onChange={handleInputs}
                  placeholder="Enter your name"
                />
              </div>
            </div>
          )}
          <div className="mt-2">
            <label htmlFor="email" className="text-[#4E5674] font-[500]">
              Email *
            </label>
            <div className="mb-4 mt-1 flex items-center rounded-lg border-2 py-2 px-3">
              <input
                className="border-none outline-none bg-transparent w-full "
                type="text"
                name="email"
                id="email"
                onChange={handleInputs}
                placeholder="Enter your email address"
              />
            </div>
          </div>

          <div className="mt-2">
            <label htmlFor="password" className="text-[#4E5674] font-[500] ">
              Password *
            </label>
            <div className="mt-1 flex items-center rounded-lg border-2 py-2 px-3">
              <input
                className="border-none outline-none bg-transparent w-full "
                type="password"
                name="password"
                id="password"
                onChange={handleInputs}
                placeholder={
                  currentRoute === "signup"
                    ? "Create a password (min. 8 characters)"
                    : "Enter your Password"
                }
              />
            </div>
          </div>

          {currentRoute === "signup" ? (
            <div className="flex justify-start mt-3">
              <input
                type="checkbox"
                className="w-3 h-3 mt-1 text-[#031927] bg-gray-100 accent-gray-100"
                onChange={(e) =>
                  setLoginData({
                    ...loginData,
                    termsAccepted: e.target.checked,
                  })
                }
              />
              <p className="text-[##2970FF] font-Plus text-sm font-[500] ml-2">
                {" "}
                I agree to the{" "}
                <Link
                  to="/privacy-policy"
                  className="font-normal text-sm text-green-500 "
                >
                  Terms & Conditions
                </Link>{" "}
              </p>
            </div>
          ) : (
            <div className="flex mt-3">
              <p
                onClick={() => {
                  navigate("/forgot-password");
                }}
                className="font-[500] text-green-500 cursor-pointer  text-sm"
              >
                Forgot password
              </p>
            </div>
          )}

          {loading ? (
            <button
              disabled
              type="button"
              className="font-base mt-6 mb-2 block w-full rounded-lg bg-primary py-2.5 text-white"
            >
              <svg
                role="status"
                className="inline mr-3 w-4 h-4 text-white animate-spin"
                viewBox="0 0 100 101"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                  fill="#E5E7EB"
                />
                <path
                  d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                  fill="currentColor"
                />
              </svg>
              Loading...
            </button>
          ) : (
            <button
              type="submit"
              className="font-medium  mt-6 mb-1 block w-full rounded-xl transition duration-200 ease-in bg-green-500 hover:bg-green-600 py-2.5 text-white"
            >
              {currentRoute === "signup" ? " Create Account" : "Sign in"}
            </button>
          )}
        </form>
        
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            disabled={googleLoading}
            className={`font-medium my-2 w-full rounded-xl transition duration-200 ease-in border border-green-500 py-2.5 flex items-center justify-center gap-x-3 px-1 text-sm ${
              googleLoading ? "bg-gray-100 text-gray-400 cursor-not-allowed" : "hover:bg-gray-100 text-gray-600"
            }`}
            onClick={handleGoogleAuthClick}
          >
            {" "}
            <FcGoogle className="h-6 w-6" />{" "}
            {currentRoute === "signup"
              ? "Sign Up with Google"
              : "Signin with Google"}
          </button>
          <button
            type="button"
            className={`font-medium my-2 w-full rounded-xl transition duration-200 ease-in border border-green-500 py-2.5 flex items-center justify-center gap-x-3 px-1 text-sm ${
              appleLoading ? "bg-gray-100 text-gray-400 cursor-not-allowed" : "hover:bg-gray-100 text-gray-600"
            }`}
            disabled={appleLoading}
            onClick={handleAppleAuthClick}
          >
            {" "}
            <FaApple className="h-6 w-6 text-gray-700" />{" "}
            {currentRoute === "signup"
              ? " Signup with Apple"
              : "Signin with Apple"}
          </button>
        </div>
        {currentRoute === "signup" ? (
          <p className="text-[#475467]  text-md text-center mt-3">
            Already have an account?
            <Link
              className="text-green-500 ml-1 font-[500] "
              to="/signin"
              replace
            >
              Login
            </Link>
          </p>
        ) : (
          <p className="text-[#475467]  text-md text-center mt-6">
            Don’t have an account?{" "}
            <Link
              className="text-green-500 ml-1 font-[500] "
              to="/signup"
              replace
            >
              Sign Up
            </Link>
          </p>
        )}
      </div>
    </div>
  );
}

export default Authentication;